import type { Paginated, SessionDetailDto, SessionDto } from '@bnn/shared';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { kv } from '../lib/kv';
import { useOnline } from '../lib/online';

/**
 * Sessions for the counting screens, with the last copy kept on the device so a counter can keep
 * counting (and reopen the page) without a network. The server stays the authority: entries sent
 * for a session that was closed meanwhile are rejected and kept as sync errors.
 */
type Cached<T> = { data: T | null; error: Error | null; offline: boolean; reload: () => void };

function useCached<T>(path: string, key: string): Cached<T> {
  const online = useOnline();
  const [state, setState] = useState<{ data: T | null; error: Error | null; offline: boolean }>({ data: null, error: null, offline: false });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await api.get<T>(path);
        await kv.set(key, d);
        if (alive) setState({ data: d, error: null, offline: false });
      } catch (e) {
        const cached = await kv.get<T>(key);
        const offline = e instanceof ApiError && e.code === 'NETWORK';
        if (!alive) return;
        if (offline && cached) setState({ data: cached, error: null, offline: true });
        else setState({ data: null, error: e instanceof Error ? e : new Error(String(e)), offline });
      }
    })();
    return () => {
      alive = false;
    };
    // re-fetch when the network comes back
  }, [path, key, tick, online]);

  // The server can be down while the device still has a network (navigator.onLine stays true):
  // keep retrying while we are showing the cached copy.
  useEffect(() => {
    if (!state.offline) return;
    const t = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(t);
  }, [state.offline]);

  return { ...state, reload: () => setTick((t) => t + 1) };
}

export const useCachedSession = (id: string) => useCached<SessionDetailDto>(`/sessions/${id}`, `session:${id}`);

export const useCachedOpenSessions = () => useCached<Paginated<SessionDto>>('/sessions?status=OPEN&page_size=50', 'open_sessions');
